import { IMeetingInfo } from "@/common/entity/Entity";
import { DEFAULT_TIMEZONE } from "@/common/entity/Enum";
import { ITreeInfo } from "@/components/ui/TreeSelect/Models";

export enum EType {
  External = "External",
}

export interface IResourceType {
  key: string;
  text: string;
}

export interface IOrganizationInfo {
  guid?: string;
  name?: string;
  adTenantId?: string;
  tenantId?: string;
  type?: string;
  memberOfs?: ITransitiveMemberOfGroup[];
  iCalUId?: string; //only for internal
  oldAttendee?: string[];
  currentDate?: string;
  isUpdateOldDataUpdate?: boolean;
  iCalUIds?: IICalUidExternal[] | null; //only for external
  uidOutlook?: string;
  resourceCentralEndpoint?: string;
  resourceTypes: IResourceType[];
  disableOrderExternal?: boolean;
}

export class OrganizationInfo {
  readonly guid: string;
  readonly name: string;
  readonly adTenantId: string;
  readonly tenantId: string;
  readonly type: string;
  readonly memberOfs: TransitiveMemberOfGroup[];
  readonly resourceTypes: IResourceType[];
  iCalUId?: string; //only for internal
  oldAttendee?: string[];
  currentDate?: string;
  isUpdateOldDataUpdate?: boolean = undefined;
  iCalUIds?: IICalUidExternal[] | null; //only for external
  uidOutlook?: string;
  resourceCentralEndpoint?: string;
  disableOrderExternal?: boolean;
  constructor(data?: IOrganizationInfo) {
    this.guid = data?.guid || "";
    this.name = data?.name || "";
    this.adTenantId = data?.adTenantId || "";
    this.tenantId = data?.tenantId || "";
    this.type = data?.type || "";
    this.resourceCentralEndpoint = data?.resourceCentralEndpoint || "";
    this.disableOrderExternal = data?.disableOrderExternal || false;
    this.memberOfs = data?.memberOfs?.length
      ? data.memberOfs.map(
          (item: ITransitiveMemberOfGroup) => new TransitiveMemberOfGroup(item)
        )
      : [];
    this.iCalUId = data?.iCalUId;
    this.iCalUIds = data?.iCalUIds;
    this.uidOutlook = data?.uidOutlook;
    this.resourceTypes = data?.resourceTypes || [];
  }

  get isExternal(): boolean {
    return this.type === EType.External;
  }

  toJson(): IOrganizationInfo {
    return JSON.parse(JSON.stringify(this));
  }
}

export interface IApiKey {
  name?: string;
  key?: string;
  expiredTime?: string;
}

export class ApiKey {
  readonly name: string;
  readonly key: string;
  readonly expiredTime: string;

  constructor(data?: IApiKey) {
    this.name = data?.name || "";
    this.key = data?.key || "";
    this.expiredTime = data?.expiredTime || "";
  }

  toJson(): IApiKey {
    return JSON.parse(JSON.stringify(this));
  }
}

export interface ITransitiveMemberOfGroup {
  id?: string;
  displayName?: string;
  mail?: string;
  mailEnabled?: boolean;
  mailNickname?: string;
  securityEnabled?: boolean;
}

export class TransitiveMemberOfGroup {
  readonly id: string;
  readonly displayName: string;
  readonly mail: string;
  readonly mailEnabled: boolean;
  readonly mailNickname: string;
  readonly securityEnabled: boolean;

  constructor(data?: ITransitiveMemberOfGroup) {
    this.id = data?.id || "";
    this.displayName = data?.displayName || "";
    this.mail = data?.mail || "";
    this.mailEnabled = !!data?.mailEnabled;
    this.mailNickname = data?.mailNickname || "";
    this.securityEnabled = !!data?.securityEnabled;
  }

  toJson(): ITransitiveMemberOfGroup {
    return JSON.parse(JSON.stringify(this));
  }
}

export interface ICategorization {
  locations: ITreeInfo[];
  categories: ITreeInfo[];
}

export interface IOrganizer {
  Name?: string;
  Email?: string;
  TimeZoneStandardName?: string;
}

export class Organizer {
  name: string;
  email: string;
  timeZone: string;

  constructor(data?: IOrganizer) {
    this.name = data?.Name || "";
    this.email = data?.Email || "";
    this.timeZone = data?.TimeZoneStandardName || DEFAULT_TIMEZONE;
  }

  toJson(): IOrganizer {
    return {
      Name: this.name,
      Email: this.email,
      TimeZoneStandardName: this.timeZone,
    };
  }
}

export interface GetOrganizerResponse {
  Name: string;
  Email: string;
  TimeZoneStandardName: string;
  OrgInfo?: OrganizationInfo | null;
}

export interface IGetApptInfoFromItemId {
  id: string;
  iCalUId: string;
  attendees?: { emailAddress: { address: string } }[];
  locations?: {
    displayName: string;
    uniqueId: string;
  }[];
  start?: { dateTime: string; timeZone: string };
}

export interface IConvertDateTimeToUTCReq {
  MeetingInfos: IMeetingInfo[];
  TimeZone: string;
}

export interface IConvertDateTimeToUTCResp {
  meetingInfos: { startTime: string; endTime: string }[];
}

export interface IICalUidExternal {
  resourceId: string;
  adId: string;
  iCalUids: string[];
}